const DEFAULT_SLIDE_INTERVAL_MS = 2000;
const DEFAULT_CROSSFADE_DURATION_MS = 1000;
const DEFAULT_ENTRANCE_DURATION_MS = 1600;
// はんこ演出尺（SCSS の stamp-sequence-duration と揃える）
const DEFAULT_STAMP_SEQUENCE_MS = 2400;
const MD_BREAKPOINT_PX = 750;
const OPENING_TRIM_END_SEC = 4;
const OPENING_FALLBACK_MS = 15000;
// オープニング後の表示順: 2 → 3 → 1 → 2 …
const FIRST_SLIDE_INDEX = 1;

/**
 * 表示中のオープニング動画を取得（PC/SP の表示切替に合わせる）
 */
function getActiveOpeningVideo(root: HTMLElement): HTMLVideoElement | null {
  const isDesktop = window.matchMedia(`(min-width: ${MD_BREAKPOINT_PX}px)`).matches;
  const selector = isDesktop
    ? ".p-top-main-visual__opening-video--pc"
    : ".p-top-main-visual__opening-video--sp";

  return root.querySelector<HTMLVideoElement>(selector);
}

/**
 * 非表示側のオープニング動画を停止
 */
function pauseInactiveOpeningVideos(root: HTMLElement): void {
  const activeVideo = getActiveOpeningVideo(root);
  root.querySelectorAll<HTMLVideoElement>("[data-js-top-main-visual-opening-video]").forEach((video) => {
    if (video !== activeVideo) {
      video.pause();
      video.currentTime = 0;
    }
  });
}

/**
 * スライド画像をデコードして切り替え時のカクつきを防ぐ
 */
async function decodeSlideImages(slide: HTMLElement): Promise<void> {
  const images = slide.querySelectorAll("img");
  await Promise.all(
    Array.from(images).map(
      (img) =>
        new Promise<void>((resolve) => {
          const decodeImage = (): void => {
            if (typeof img.decode === "function") {
              img.decode().then(() => resolve()).catch(() => resolve());
              return;
            }
            resolve();
          };

          if (img.complete) {
            decodeImage();
            return;
          }

          img.addEventListener("load", decodeImage, { once: true });
          img.addEventListener("error", () => resolve(), { once: true });
        }),
    ),
  );
}

/**
 * 動画再生中に全スライド画像を先読みする
 */
function preloadAllSlideImages(slides: NodeListOf<HTMLElement>): void {
  slides.forEach((slide) => {
    void decodeSlideImages(slide);
  });
}

/**
 * 動画を停止（ラストフレームは表示したまま）
 */
function pauseOpeningVideo(video: HTMLVideoElement | null): void {
  if (!video) return;
  video.pause();
}

/**
 * クロスフェード完了後に動画デコーダーを解放する
 */
function releaseOpeningVideo(video: HTMLVideoElement | null): void {
  if (!video) return;
  video.pause();
  video.removeAttribute("src");
  video.load();
}

/**
 * リフローを発生させて CSS transition を確実に起動する
 */
function forceReflow(element: HTMLElement): void {
  void element.offsetWidth;
}

/**
 * ヘッダー・スクロールUIをオープニング後に表示
 */
function scheduleTopMainVisualUiReveal(root: HTMLElement, delayMs = 0): void {
  const revealUi = (): void => {
    root.classList.remove("is-opening");
    root.classList.add("is-ui-visible");
  };

  const entranceDuration = Number(root.dataset.entranceDuration) || DEFAULT_ENTRANCE_DURATION_MS;
  window.setTimeout(revealUi, delayMs + entranceDuration);
}

/**
 * スクロールボタン：次セクションへスムーススクロール
 */
function initTopMainVisualScroll(root: HTMLElement): void {
  const button = root.querySelector<HTMLButtonElement>("[data-js-top-main-visual-scroll]");
  if (!button) return;

  button.addEventListener("click", () => {
    const nextSection = root.nextElementSibling;
    if (!(nextSection instanceof HTMLElement)) return;

    nextSection.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

/**
 * トップメインビジュアル：はんこ演出 → オープニング動画 → スライドショー
 */
function initTopMainVisual(root: HTMLElement): void {
  if (root.dataset.jsTopMainVisualInitialized === "true") return;

  const stamp = root.querySelector<HTMLElement>("[data-js-top-main-visual-stamp]");
  const stampImage = root.querySelector<HTMLElement>(".p-top-main-visual__stamp-image");
  const opening = root.querySelector<HTMLElement>("[data-js-top-main-visual-opening]");
  const slidesContainer = root.querySelector<HTMLElement>("[data-js-top-main-visual-slides]");
  const slides = root.querySelectorAll<HTMLElement>("[data-js-top-main-visual-slide]");

  if (!opening || !slidesContainer || slides.length === 0) return;

  root.dataset.jsTopMainVisualInitialized = "true";

  initTopMainVisualScroll(root);
  preloadAllSlideImages(slides);

  const slideInterval = Number(root.dataset.slideInterval) || DEFAULT_SLIDE_INTERVAL_MS;
  const crossfadeDuration =
    Number(root.dataset.crossfadeDuration) || DEFAULT_CROSSFADE_DURATION_MS;

  root.style.setProperty("--top-main-visual-crossfade-duration", `${crossfadeDuration}ms`);

  let currentSlideIndex = FIRST_SLIDE_INDEX;
  let slideshowStarted = false;
  let slideTimerId: number | null = null;

  const clearSlideTimer = (): void => {
    if (slideTimerId !== null) {
      window.clearTimeout(slideTimerId);
      slideTimerId = null;
    }
  };

  const advanceSlide = (): void => {
    const nextIndex = (currentSlideIndex + 1) % slides.length;
    const nextSlide = slides[nextIndex];
    if (!nextSlide) return;

    void decodeSlideImages(nextSlide).then(() => {
      currentSlideIndex = nextIndex;
      requestAnimationFrame(() => {
        showSlide(currentSlideIndex);
      });
    });
  };

  /**
   * クロスフェード完了後に slideInterval 待ってから次スライドへ
   * （setInterval だと2枚目以降だけ間隔が短くなるため setTimeout で統一）
   */
  const scheduleNextSlide = (useCrossfadeDelay: boolean): void => {
    clearSlideTimer();
    const delay = useCrossfadeDelay ? crossfadeDuration + slideInterval : slideInterval;
    slideTimerId = window.setTimeout(() => {
      advanceSlide();
      scheduleNextSlide(true);
    }, delay);
  };

  const showSlide = (index: number): void => {
    slides.forEach((slide, i) => {
      slide.classList.toggle("is-active", i === index);
    });
  };

  const clearAllSlides = (): void => {
    slides.forEach((slide) => {
      slide.classList.remove("is-active");
    });
  };

  /**
   * 動画終了後、スライド2へクロスフェードで遷移
   */
  const crossfadeToFirstSlide = (video: HTMLVideoElement | null): void => {
    const targetSlide = slides[FIRST_SLIDE_INDEX];
    if (!targetSlide) return;

    // ラストフレームを残したまま pause（src 削除はクロスフェード後）
    pauseOpeningVideo(video);

    opening.classList.add("is-ready");
    clearAllSlides();
    forceReflow(opening);
    forceReflow(targetSlide);

    // スライド2を先に表示してから動画をフェードアウト（空白フレームを防ぐ）
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        targetSlide.classList.add("is-active");
        forceReflow(targetSlide);
        opening.classList.add("is-fade-out");
      });
    });

    window.setTimeout(() => {
      releaseOpeningVideo(video);
      opening.remove();
    }, crossfadeDuration);
  };

  const startSlideshow = (video: HTMLVideoElement | null, useCrossfadeDelay: boolean): void => {
    if (slideshowStarted) return;
    slideshowStarted = true;

    root.classList.add("is-slideshow-active");
    slidesContainer.setAttribute("aria-hidden", "false");
    crossfadeToFirstSlide(video);
    scheduleNextSlide(useCrossfadeDelay);
  };

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReducedMotion) {
    stamp?.remove();
    root.classList.remove("is-stamp-pending");
    opening.remove();
    slideshowStarted = true;
    root.classList.add("is-slideshow-active");
    slidesContainer.setAttribute("aria-hidden", "false");
    root.classList.remove("is-opening");
    root.classList.add("is-ui-visible");
    showSlide(FIRST_SLIDE_INDEX);
    scheduleNextSlide(false);
    return;
  }

  const startVideoOpening = (): void => {
    root.classList.remove("is-stamp-pending");
    root.classList.add("is-opening-started");
    scheduleTopMainVisualUiReveal(root);

    const video = getActiveOpeningVideo(root);
    if (!video) {
      startSlideshow(null, true);
      return;
    }

    pauseInactiveOpeningVideos(root);

    const fallbackTimerId = window.setTimeout(() => {
      onOpeningEnd();
    }, OPENING_FALLBACK_MS);

    let openingEnded = false;

    const onOpeningEnd = (): void => {
      if (openingEnded) return;
      openingEnded = true;
      window.clearTimeout(fallbackTimerId);
      video.removeEventListener("timeupdate", onOpeningTimeUpdate);
      startSlideshow(video, true);
    };

    // ラスト4秒（スライド1と同画像の区間）を省略して早めにクロスフェードへ
    const onOpeningTimeUpdate = (): void => {
      if (!Number.isFinite(video.duration) || video.duration <= OPENING_TRIM_END_SEC) return;
      if (video.currentTime >= video.duration - OPENING_TRIM_END_SEC) {
        onOpeningEnd();
      }
    };

    video.addEventListener("timeupdate", onOpeningTimeUpdate);
    video.addEventListener("ended", onOpeningEnd, { once: true });

    preloadAllSlideImages(slides);

    video.play().catch(() => {
      onOpeningEnd();
    });
  };

  const finishStampSequence = (): void => {
    stamp?.remove();
    startVideoOpening();
  };

  if (stamp && stampImage) {
    const stampFallbackTimerId = window.setTimeout(finishStampSequence, DEFAULT_STAMP_SEQUENCE_MS + 500);

    stampImage.addEventListener(
      "animationend",
      () => {
        window.clearTimeout(stampFallbackTimerId);
        finishStampSequence();
      },
      { once: true },
    );
    return;
  }

  finishStampSequence();
}

export function initTopMainVisualAll(): void {
  document.querySelectorAll<HTMLElement>("[data-js-top-main-visual]").forEach(initTopMainVisual);
}
